/**
 * Utility for extracting user-readable messages from RFC 7807 ProblemDetails API errors.
 *
 * Design principle: error display is intentionally simple — every call site shows the
 * backend's own message, falling back to a caller-supplied string when none exists:
 *
 *   showSnackbar(getProblemDetail(error) ?? 'Failed to save record', 'error');
 *
 * For logs and non-snackbar UI that always need a string:
 *
 *   log.error('…', { error: formatErrorMessage(error) });
 *
 * The `??` fallback is permanent. Status-only errors (e.g. return NotFound() with no detail)
 * and empty bodies make getProblemDetail return undefined — that is expected, not a bug.
 * See docs/ERROR_HANDLING.md.
 *
 * Prefer these helpers everywhere instead of reading error.message on ApiException
 * (NSwag's generic "A server side error occurred.").
 */

import { ApiException, type ProblemDetails } from '@/api/ApiClient';
import { reportApiContractDeviation } from '@/lib/utils/reportApiContractDeviation';

/**
 * Trims whitespace and strips a single pair of surrounding double quotes.
 * Legacy quoted string bodies (e.g. `"message"`) are reported then cleaned once here.
 */
function normalizeDetail(value: string): string {
  const trimmed = value.trim();
  const dequoted = trimmed.replace(/^"|"$/g, '').trim();
  if (dequoted !== trimmed) {
    reportApiContractDeviation('Stripped surrounding quotes from error detail (legacy JSON string body).', {
      original: trimmed.slice(0, 200),
    });
  }
  return dequoted;
}

/**
 * Extracts the human-readable `detail` string from an RFC 7807 ProblemDetails error body.
 *
 * Prefer this over `error.result` (object → "[object Object]") and over `error.message`
 * (NSwag's generic "A server side error occurred.").
 *
 * @returns the cleaned `detail`, or undefined when the error is not an ApiException,
 *   has no body, or has no/blank `detail` (status-only 404/400 etc. — use `??` fallback).
 */
export function getProblemDetail(error: unknown): string | undefined {
  if (ApiException.isApiException(error)) {
    const result = error.result as ProblemDetails | string | null | undefined;
    // Normal case: ProblemDetails object with a `detail` field.
    const detail = typeof result === 'object' && result !== null ? result.detail : undefined;
    if (typeof detail === 'string' && detail.trim()) {
      return normalizeDetail(detail);
    }
    // Defensive: plain string body — report, then still return text for the snackbar.
    if (typeof result === 'string' && result.trim()) {
      reportApiContractDeviation(
        'ApiException.result is a plain string instead of ProblemDetails; using it as detail.',
        {
          status: error.status,
          preview: result.slice(0, 200),
        }
      );
      return normalizeDetail(result);
    }
  }
  return undefined;
}

/**
 * Always returns a string for logs or on-page error text.
 * Prefer ProblemDetails.detail; for ApiException do not fall back to NSwag's generic
 * error.message when detail is missing. Local Error.message is used only for non-API throws.
 */
export function formatErrorMessage(error: unknown, fallback = 'Unknown error'): string {
  if (ApiException.isApiException(error)) {
    return getProblemDetail(error) ?? fallback;
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  if (typeof error === 'string' && error.trim()) {
    return error;
  }
  return fallback;
}
